import { useEffect } from 'react';

import { Container, Flex } from '@grupoboticario/flora-react';
import { useLocation, useParams } from 'react-router-dom';

import {
  ErrorBoundaries,
  Skeleton,
  PageInfo,
  ButtonBreadcrumb,
  ResponsiveFloraTypography,
} from 'components';
import { getShortDate, getShortCycle, hasError, getOrderStatus } from 'helpers';
import { useOrderDetail, useTracking } from 'hooks';

import { ItemsAccordion, Payment, Summary, Tracking } from './components';
import Styles from './Order.styles';

export const Order = () => {
  const { id = '0' } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const { data: order, failureReason, isLoading, isError, isSuccess } = useOrderDetail(id);
  const { trackWithUserData } = useTracking();

  useEffect(() => {
    if (isSuccess && !isLoading && order) {
      const status = getOrderStatus({
        status: order.status,
        dateTime: order.orderDate,
      }).tagText.toLocaleLowerCase();

      trackWithUserData({
        transaction_id: order.id,
        cd_metodo_pagamento: order.payment?.[0]?.label || '',
        cd_status_pedido: status,
        cd_total_itens_comprados: order.orderItems?.standard?.length || 0,
        cd_total_brindes: order.orderItems?.gift?.length || 0,
        cd_total_itens_cortados: order.orderItems?.cut?.length || 0,
        cd_dias_apos_vencimento: order.payment?.[0]?.titles?.[0]?.daysUntilDueDate,
        cd_dias_para_vencimento: order.payment?.[0]?.titles?.[0]?.daysLate,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (isError && !isLoading) {
      trackWithUserData({
        cd_error_message: 'tivemos-um-problema-para-carregar-o-detalhe-do-pedido',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  if (isError || hasError(failureReason)) {
    return (
      <Styles.OrderWrapper data-autotagging-flora-section='detalhes_pedido'>
        <ErrorBoundaries.ReloadPage title='Tivemos um problema para carregar o detalhe do pedido' />
      </Styles.OrderWrapper>
    );
  }

  return (
    <Styles.OrderWrapper>
      <PageInfo
        breadcrumbs={[
          {
            as: ButtonBreadcrumb,
            text: 'Pedidos',
            url: '/pedidos?tab=historico',
          },
          {
            text: `Detalhes do Pedido`,
            url: pathname,
          },
        ]}
      >
        {Boolean(isLoading) ? (
          <>
            <Skeleton data-testid='skeleton-heading' height='$10' width='25%' />

            <Skeleton data-testid='skeleton-text' width='50%' />
          </>
        ) : (
          <>
            <ResponsiveFloraTypography fontSize='title' fontWeight='bold' as='h1'>
              Pedido {order?.id}
            </ResponsiveFloraTypography>
            <ResponsiveFloraTypography>
              Detalhes do pedido realizado em {getShortDate(order?.orderDate || '')} - Ciclo{' '}
              {getShortCycle(order?.cycle || '')}
            </ResponsiveFloraTypography>
          </>
        )}
      </PageInfo>

      <Styles.Wrapper>
        <Container size='large' data-autotagging-flora-section='detalhes_pedido'>
          <Styles.Grid>
            <Tracking />
            <ItemsAccordion />
            <Flex direction='column' gap='$3'>
              <Summary />
              <Payment />
            </Flex>
          </Styles.Grid>
        </Container>
      </Styles.Wrapper>
    </Styles.OrderWrapper>
  );
};

export default Order;
